import { useState } from "react"
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Pen,
  ShieldCheck,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DatePopover } from "@/components/date-popover"
import { DocSlotCard } from "@/features/transit/components/doc-slot-card"
import { LockedBanner } from "@/features/transit/components/locked-banner"
import { SectionTitle } from "@/features/transit/components/section-title"
import { UploadButton } from "@/features/transit/components/upload-button"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import {
  useAckDraftBL,
  useMarkDefinitiveBL,
  useRegisterDraftBL,
  useUnmarkDefinitiveBL,
} from "@/features/transit/hooks/use-doc-mutations"
import { usePatchOT } from "@/features/transit/hooks/use-transit-mutations"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import {
  isOTLocked,
  latestDraftBL,
  mismatchSummary,
} from "@/features/transit/lib/ot-math"
import type { OT, OTDraftBL } from "@/features/transit/api/types"

interface EmbarquementPanelProps {
  ot: OT
}

export function EmbarquementPanel({ ot }: EmbarquementPanelProps) {
  const locked = isOTLocked(ot)
  return (
    <div className="flex flex-col gap-4">
      {locked ? <LockedBanner ot={ot} /> : null}
      <DraftBLCard ot={ot} />
      <ConfirmationCard ot={ot} />
    </div>
  )
}

// Embarkment confirmation slot — wrapped with a SectionTitle that
// explains what's expected (a screenshot or email evidencing that the
// shipping line confirmed the booking).
function ConfirmationCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const patch = usePatchOT(ot.id)
  const locked = isOTLocked(ot)
  return (
    <Card className="flex flex-col gap-3 p-5">
      <SectionTitle
        eyebrow={t("embarquement.confirmationEyebrow")}
        title={t("embarquement.confirmationTitle")}
        description={t("embarquement.confirmationDescription")}
      />
      <div className="grid max-w-xs flex-col gap-1.5">
        <Label>{t("embarquement.vesselDepartedAt")}</Label>
        <DatePopover
          value={ot.vesselDepartedAt ?? ""}
          disabled={locked}
          onChange={(iso) => {
            if (iso !== (ot.vesselDepartedAt ?? "")) {
              patch.mutate({ vesselDepartedAt: iso })
            }
          }}
        />
      </div>
      <DocSlotCard
        ot={ot}
        slotId="embarkmentConfirmation"
        hint={t("embarquement.confirmationHint")}
      />
    </Card>
  )
}

function DraftBLCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const upload = useTransitUpload()
  const register = useRegisterDraftBL(ot.id)
  const unmark = useUnmarkDefinitiveBL(ot.id)
  const locked = isOTLocked(ot)

  const [weight, setWeight] = useState("")
  const [markDialogDraft, setMarkDialogDraft] = useState<OTDraftBL | null>(null)
  const versions = [...(ot.draftBLs ?? [])].sort(
    (a, b) => a.versionIdx - b.versionIdx
  )
  const latest = latestDraftBL(ot)
  const m = mismatchSummary(ot)

  const marker = ot.okPourImpression
  // The "marquer définitif" action is available as soon as the OT has
  // at least one draft BL version and no marker is set yet. Server-side
  // mismatch-ack rules still apply per chosen version.
  const canMarkAny = !locked && versions.length > 0 && !marker

  const meta = useUploadWithMetadata({
    kindLabel: t("embarquement.draftBLTitle"),
    upload: async (file, m) => {
      const w = Number(weight) || 0
      if (w <= 0) return
      await upload.upload({
        otId: ot.id,
        kind: "draftBL",
        file,
        register: async ({ storedKey, file: f }) =>
          register.mutateAsync({
            name: m.name,
            storedKey,
            docType: f.type || undefined,
            weightKg: w,
            uploadedBy: m.uploadedBy || undefined,
            note: m.description || undefined,
          }),
      })
      setWeight("")
    },
  })

  return (
    <Card className="flex flex-col gap-4 p-5">
      <SectionTitle
        eyebrow={t("embarquement.eyebrow")}
        title={t("embarquement.draftBLTitle")}
      />

      {/* Inline weight readout — surface the empotage weight straight
          away so the operator knows the target before they type the BL
          weight, then once a BL is uploaded show both values plus the
          delta. Mismatch acknowledgement and the explanation live in
          the OK pour impression card. */}
      {m.stuffedKg != null || m.blKg != null ? (
        <WeightDiffReadout
          stuffedKg={m.stuffedKg}
          blKg={m.blKg}
          mismatch={m.blMismatch}
        />
      ) : null}

      <div>
        {locked ? (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <History className="size-3.5" />
            <span className="font-semibold tracking-wide uppercase">
              {t("embarquement.versionsHeader")}
            </span>
            <span className="tabular-nums">· {versions.length}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm">
                  {t("embarquement.weightLabel")}
                </Label>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <History className="size-3.5" />
                  <span className="font-semibold tracking-wide uppercase">
                    {t("embarquement.versionsHeader")}
                  </span>
                  <span className="tabular-nums">· {versions.length}</span>
                </div>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={t("embarquement.weightPlaceholder")}
                  className="pr-12"
                />
                <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted-foreground">
                  kg
                </span>
              </div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <UploadButton
                    onPick={meta.onPick}
                    isUploading={upload.isUploading || register.isPending}
                    disabled={!weight || Number(weight) <= 0}
                    label={t("embarquement.uploadVersion")}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("embarquement.uploadVersionHelp")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {versions.length > 0 ? (
          <div className="mt-3 overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("embarquement.cols.version")}</TableHead>
                  <TableHead>{t("embarquement.cols.when")}</TableHead>
                  <TableHead className="text-right">
                    {t("embarquement.cols.weight")}
                  </TableHead>
                  <TableHead>{t("embarquement.cols.name")}</TableHead>
                  <TableHead className="text-right">
                    {t("embarquement.cols.definitive")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((v) => {
                  const isMarked = marker?.draftId === v.id
                  return (
                    <TableRow
                      key={v.id}
                      className={
                        isMarked
                          ? "bg-emerald-50/70 dark:bg-emerald-500/10"
                          : v.versionIdx === latest?.versionIdx
                            ? "bg-muted/40"
                            : undefined
                      }
                    >
                      <TableCell className="tabular-nums">
                        {isMarked
                          ? t("embarquement.versionNameDefinitive")
                          : t("embarquement.versionName", {
                              n: v.versionIdx + 1,
                            })}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.uploadedAt?.slice(0, 10)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {v.weightKg.toLocaleString()} kg
                      </TableCell>
                      <TableCell className="truncate text-xs text-muted-foreground">
                        {v.name}
                      </TableCell>
                      <TableCell className="text-right">
                        {isMarked ? (
                          <DefinitiveCell
                            marker={marker}
                            onUnmark={() => unmark.mutate()}
                            unmarking={unmark.isPending}
                            disabled={locked}
                          />
                        ) : marker ? (
                          <span className="text-[11px] text-muted-foreground">
                            —
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setMarkDialogDraft(v)}
                            disabled={!canMarkAny}
                          >
                            <Pen className="size-3.5" />
                            {t("embarquement.markBtn")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </div>
      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />
      <MarkDefinitiveDialog
        ot={ot}
        draft={markDialogDraft}
        open={markDialogDraft != null}
        onOpenChange={(next) => {
          if (!next) setMarkDialogDraft(null)
        }}
      />
    </Card>
  )
}

// Inline cell rendered on the row whose version was marked definitive.
// Shows who/when + an unmark icon button. ShieldCheck visually matches
// the legacy OkPourImpression card so operators recognise the marker.
function DefinitiveCell({
  marker,
  onUnmark,
  unmarking,
  disabled,
}: {
  marker: NonNullable<OT["okPourImpression"]>
  onUnmark: () => void
  unmarking: boolean
  disabled: boolean
}) {
  const { t } = useTranslation("transit")
  return (
    <div className="flex items-center justify-end gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <ShieldCheck className="size-3" />
            {t("embarquement.definitiveChip")}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t("embarquement.definitiveTooltip", {
            by: marker.by,
            at: marker.at?.slice(0, 10) ?? "—",
          })}
        </TooltipContent>
      </Tooltip>
      {disabled ? null : (
        <Button
          variant="ghost"
          size="sm"
          onClick={onUnmark}
          disabled={unmarking}
        >
          {t("embarquement.unmarkBtn")}
        </Button>
      )}
    </div>
  )
}

// Inline weight comparison for the BL versions area. Shows stuffed vs
// BL with the delta — same numbers a manager would compute by hand.
// Mismatch is amber-toned but doesn't carry an action; the action lives
// on the OK pour impression card below.
function WeightDiffReadout({
  stuffedKg,
  blKg,
  mismatch,
}: {
  stuffedKg: number | null
  blKg: number | null
  mismatch: boolean
}) {
  const { t } = useTranslation("transit")
  // Delta only meaningful once both sides are present. Before the BL is
  // uploaded we still render the bar so the operator sees the target
  // empotage weight; the BL slot reads "—" until they upload.
  const haveBoth = stuffedKg != null && blKg != null
  const delta = haveBoth ? blKg! - stuffedKg! : null
  const sign = delta != null ? (delta > 0 ? "+" : delta < 0 ? "−" : "") : ""
  const absDelta = delta != null ? Math.abs(delta) : null
  return (
    <div
      className={
        mismatch
          ? "flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs dark:border-amber-500/30 dark:bg-amber-500/10"
          : "flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-muted/30 px-3 py-2 text-xs"
      }
    >
      {mismatch ? (
        <AlertTriangle className="size-3.5 flex-shrink-0 text-amber-600" />
      ) : (
        <CheckCircle2 className="size-3.5 flex-shrink-0 text-emerald-600" />
      )}
      <DiffStat
        label={t("embarquement.stuffedKg")}
        value={
          stuffedKg != null ? `${stuffedKg.toLocaleString()} kg` : "—"
        }
      />
      <span className="text-muted-foreground">vs</span>
      <DiffStat
        label={t("embarquement.draftBLWeight")}
        value={blKg != null ? `${blKg.toLocaleString()} kg` : "—"}
      />
      <span className="text-muted-foreground">·</span>
      <DiffStat
        label={t("embarquement.diffLabel")}
        value={
          absDelta != null ? (
            <span
              className={mismatch ? "text-amber-700 dark:text-amber-300" : ""}
            >
              {sign}
              {absDelta.toLocaleString()} kg
            </span>
          ) : (
            "—"
          )
        }
      />
    </div>
  )
}

function DiffStat({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5 tabular-nums">
      <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      <span className="text-foreground font-medium">{value}</span>
    </span>
  )
}

// MarkDefinitiveDialog drives both the manager name + checkbox flow
// (carried over from the old OkPourImpression card) and the mismatch
// acknowledgement when the picked version doesn't match the stuffed
// weight. Hosted by DraftBLCard so the version table rows can open it
// against a specific draft.
function MarkDefinitiveDialog({
  ot,
  draft,
  open,
  onOpenChange,
}: {
  ot: OT
  draft: OTDraftBL | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation("transit")
  const markMut = useMarkDefinitiveBL(ot.id)
  const ackMut = useAckDraftBL(ot.id)
  const [name, setName] = useState("")
  const [agree, setAgree] = useState(false)
  const [agreeMismatch, setAgreeMismatch] = useState(false)

  // Mismatch is computed against the *picked* draft, not the latest.
  const stuffed = ot.totalStuffedWeightKg ?? null
  const blKg = draft?.weightKg ?? null
  const mismatch =
    stuffed != null && blKg != null && Math.abs(blKg - stuffed) > 50
  const ack = ot.draftBLWeightAck
  const ackCoversThisDraft =
    !!ack && draft != null && ack.versionIdx === draft.versionIdx
  const needsAck = mismatch && !ackCoversThisDraft

  const submitting = markMut.isPending || ackMut.isPending
  const reset = () => {
    setName("")
    setAgree(false)
    setAgreeMismatch(false)
  }

  const submit = async () => {
    if (!draft) return
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      if (needsAck) await ackMut.mutateAsync({ by: trimmed })
      await markMut.mutateAsync({
        draftId: draft.id,
        payload: { by: trimmed },
      })
      onOpenChange(false)
      reset()
    } catch {
      // Hooks already toast on error; leave the dialog open with state
      // intact so the user can retry.
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("embarquement.markModalTitle")}</DialogTitle>
          <DialogDescription>
            {draft
              ? t("embarquement.markModalSubtitle", {
                  version: draft.versionIdx + 1,
                })
              : null}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-muted-foreground">
            {t("embarquement.markModalBody")}
          </p>

          {needsAck && stuffed != null && blKg != null ? (
            <MismatchSummaryBlock stuffedKg={stuffed} blKg={blKg} />
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label>{t("embarquement.okModalNameLabel")}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("embarquement.okModalNamePlaceholder")}
            />
          </div>

          {needsAck ? (
            <label className="flex items-start gap-2 text-xs">
              <Checkbox
                checked={agreeMismatch}
                onCheckedChange={(v) => setAgreeMismatch(!!v)}
                className="mt-0.5"
              />
              <span>{t("embarquement.okModalAgreeMismatch")}</span>
            </label>
          ) : null}

          <label className="flex items-start gap-2 text-xs">
            <Checkbox
              checked={agree}
              onCheckedChange={(v) => setAgree(!!v)}
              className="mt-0.5"
            />
            <span>{t("embarquement.markModalAgree")}</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("wizard.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={
              !draft ||
              !name.trim() ||
              !agree ||
              (needsAck ? !agreeMismatch : false) ||
              submitting
            }
          >
            <Pen className="size-4" />
            {t("embarquement.markModalConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Read-only mismatch summary surfaced inside the sign dialog when
// there's an un-acknowledged weight gap. The signer reads the actual
// numbers and confirms the gap as part of the same signature flow.
function MismatchSummaryBlock({
  stuffedKg,
  blKg,
}: {
  stuffedKg: number
  blKg: number
}) {
  const { t } = useTranslation("transit")
  const delta = blKg - stuffedKg
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : ""
  const absDelta = Math.abs(delta)

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 flex-shrink-0 text-amber-600" />
        <div className="flex-1 text-xs">
          <p className="font-medium text-amber-800 dark:text-amber-300">
            {t("embarquement.mismatchTitle")}
          </p>
          <p className="mt-0.5 text-foreground/80">
            {t("embarquement.mismatchExplain", {
              stuffed: stuffedKg.toLocaleString(),
              bl: blKg.toLocaleString(),
              sign,
              delta: absDelta.toLocaleString(),
            })}
          </p>
        </div>
      </div>
    </div>
  )
}
